import React, { useState, useEffect } from 'react';
import { useParams, Routes, Route } from 'react-router-dom';
import { getPortfolioBySubdomain, getPortfolioById } from '../../firebase/publicPortfolio.service';
import { getUserProfile } from '../../firebase/user.service';
import { onPropertiesChange } from '../../firebase/property.service';
import { getSubdomainInfo } from '../../utils/subdomain';
import PublicPortfolioTemplate from './PublicPortfolioTemplate';
import PublicPropertyPage from './PublicPropertyPage';

/**
 * Public Portfolio Viewer
 * Routes between portfolio listing and individual property pages
 */
const PublicPortfolioViewer = () => {
    const { propertyId, portfolioId } = useParams();
    const [portfolio, setPortfolio] = useState(null);
    const [owner, setOwner] = useState(null);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        let unsubscribe = null;

        const fetchPortfolioAndData = async () => {
            setLoading(true);
            setNotFound(false);
            setPortfolio(null);
            setOwner(null);
            setProperties([]);

            const subdomainInfo = getSubdomainInfo();

            try {
                // Determine how to fetch the portfolio: by subdomain or explicit portfolioId
                let portfolioData = null;

                if (subdomainInfo.isPublicDomain && subdomainInfo.subdomain) {
                    portfolioData = await getPortfolioBySubdomain(subdomainInfo.subdomain);
                } else if (portfolioId) {
                    portfolioData = await getPortfolioById(portfolioId);
                }

                if (!portfolioData) {
                    setNotFound(true);
                    setLoading(false);
                    return;
                }

                console.log('[PublicPortfolioViewer] portfolioData:', portfolioData);
                setPortfolio(portfolioData);

                // Portfolio should reference ownerId or userId - normalize to ownerId
                const ownerId = portfolioData.ownerId || portfolioData.userId || portfolioData.owner || null;
                console.log('[PublicPortfolioViewer] resolved ownerId:', ownerId);
                if (!ownerId) {
                    console.warn('Portfolio has no ownerId/userId');
                    setNotFound(true);
                    setLoading(false);
                    return;
                }

                // Fetch user profile (single source of truth)
                const userProfile = await getUserProfile(ownerId);
                console.log('[PublicPortfolioViewer] userProfile:', userProfile);
                if (!userProfile) {
                    setNotFound(true);
                    setLoading(false);
                    return;
                }

                setOwner(userProfile);

                // Subscribe to properties for this user (real-time)
                unsubscribe = onPropertiesChange(ownerId, (items) => {
                    console.log('[PublicPortfolioViewer] properties update:', items);
                    setProperties(items || []);
                });
                console.log('[PublicPortfolioViewer] subscribed to properties for ownerId=', ownerId);

            } catch (err) {
                console.error('[PublicPortfolioViewer] Error loading portfolio page:', err);

                // Log specific Firebase errors for debugging
                if (err?.code === 'permission-denied') {
                    console.error('[PublicPortfolioViewer] Firestore permission denied. Check security rules.');
                }

                setNotFound(true);
            } finally {
                setLoading(false);
            }
        };

        fetchPortfolioAndData();

        return () => {
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [portfolioId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
                    <p className="text-stone-600">Loading portfolio...</p>
                </div>
            </div>
        );
    }

    if (notFound) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-stone-50">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-stone-800">Portfolio not found</h1>
                    <p className="mt-2 text-stone-600">The subdomain you're looking for doesn't exist.</p>
                </div>
            </div>
        );
    }

    // Merge portfolio document and owner profile into a single page profile.
    // Prefer explicit fields from the portfolio document (public overrides) but fall back to owner profile when missing.
    const pageProfile = {
        ...(owner || {}),
        ...(portfolio || {}),
    };

    // Render property detail page if propertyId in URL
    if (propertyId) {
        const property = properties.find(p => p.id === propertyId);
        return <PublicPropertyPage property={property} portfolio={pageProfile} agentName={pageProfile?.displayName} properties={properties} />;
    }

    // Render portfolio listing page (default)
    return <PublicPortfolioTemplate portfolio={pageProfile} properties={properties} />;
};

export default PublicPortfolioViewer;
